import { AccountOutlined } from '@inspectify/design';
import { Box, Stack, useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ToolBar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { useAuth } from '../../../auth';
import { UserPermissions } from '../../../types/user';
import { useLayoutContext } from '../useLayoutContext';
import DrawerList from './DrawerList';
import { SectionType } from "../../../types";
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { useAppSelector } from "../../../redux/store";
import { SplitFlagEnum } from "../../../vendor/splitio";
import { selectTreatmentValue } from "@splitsoftware/splitio-redux";
import { ContractorTypeEnum } from "../../../pages/OnboardingFlow/types";
import InspectifyLogoSvg from '@inspectify/design/assets/brands/inspectify/InspectifyLogoColor.svg'
import InspectifyLogoWhiteSvg from '@inspectify/design/assets/brands/inspectify/InspectifyLogoWhite.svg'

const ContractorTypeFilter = [ContractorTypeEnum.Inspector, ContractorTypeEnum.Surveyor];
const ContractorDrawerContent = () => {
  const { userContext: { currentContractor } } = useAuth();
  const featureAnalytics = useAppSelector(state => selectTreatmentValue(state.splitio, SplitFlagEnum.FeatureDrawManagement)) === "on";
  const { setDrawerSections } = useLayoutContext();
  const theme = useTheme();

  useEffect(() => {
    const sections: TreeViewBaseItem<SectionType>[] = [];
    if (!currentContractor?.contractor?.id) {
      setDrawerSections(sections);
      return;
    }
    const permissions = currentContractor?.grantedPermissions;
    if (permissions?.includes(UserPermissions.ViewNewJobsTab)) {
      sections.push({ id: 'contractor', label: 'Jobs', path: '/dashboard' });
    }
    if (permissions?.includes(UserPermissions.ViewPaymentSetupPage)) {
      sections.push({
        id: 'payments',
        label: 'Payment', path: '/dashboard/payments',
        children: [
          { id: 'setup', label: 'Payment setup', path: '/dashboard/payments/setup' },
          { id: 'history', label: 'Payment History', path: '/dashboard/payments/history' },
          { id: 'invoices', label: 'Invoices History', path: '/dashboard/payments/invoices' },
        ],
      });
    }
    if (permissions?.includes(UserPermissions.ContractorAdmin)) {
      if (featureAnalytics && ContractorTypeFilter.includes(currentContractor.contractor.contractorType! as ContractorTypeEnum)) {
        sections.push({ id: 'analytics', label: 'Analytics', path: '/dashboard/analytics' });
      }
      sections.push({
        id: 'business', label: 'Business details', path: '/dashboard/business',
        children: [{
          id: 'basic', label: 'Basic Information', path: '/dashboard/business#basic',
        }, {
          id: 'services', label: 'Inspection services', path: '/dashboard/business#services',
        }, {
          id: 'certifications', label: 'Certifications', path: '/dashboard/business#certifications',
        }, {
          id: 'area', label: 'Serviceable area', path: '/dashboard/business#area',
        }, {
          id: 'tools', label: 'Inspection tools', path: '/dashboard/business#tools',
        }, {
          id: 'documents', label: 'Documents', path: '/dashboard/business#documents',
        }]
      });
    }

    if (currentContractor?.canManageUsers) {
      sections.push({ id: 'manage', label: 'Manage Users', path: '/dashboard/manage' });
    }
    setDrawerSections(sections);
  }, [currentContractor, setDrawerSections]);

  return (
    <>
      <ToolBar>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <img src={theme.palette.mode === 'dark' ? InspectifyLogoWhiteSvg : InspectifyLogoSvg} width={150} alt="Inspectify" />
        </Box>
      </ToolBar>
      <Stack spacing={2} alignItems="center">
        {currentContractor?.contractor?.logo?.url ? (
          <Avatar
            sx={{ width: 120, height: 120, mb: 2 }}
            src={currentContractor?.contractor?.logo?.url}
          />
        ) : (
          <AccountOutlined sx={{ fontSize: 120, mb: 2 }} />
        )}
        <Typography variant="h5">
          {currentContractor?.contractor?.businessName}
        </Typography>
        <Divider />
        <DrawerList />
      </Stack>
    </>
  );
};

export default ContractorDrawerContent;
